import type { Dispatch, SetStateAction } from 'react';
import { AlertCircle, AlertTriangle, Clock, ShieldAlert, Timer } from 'lucide-react';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import type { ApprovalSummary } from '@/hooks/ApprovalDashboard/useApprovalFilters';

interface ApprovalKpiCardsProps {
  summary: ApprovalSummary;
  anomalyFilter: string;
  setAnomalyFilter: Dispatch<SetStateAction<string>>;
  setOverdueFilter: Dispatch<SetStateAction<boolean>>;
}

export function ApprovalKpiCards({
  summary,
  anomalyFilter,
  setAnomalyFilter,
  setOverdueFilter,
}: ApprovalKpiCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      {/* Card 1: Total Pending */}
      <NavigationMetricCardAlt
        title="Pending Approvals"
        value={summary.pending.toString()}
        icon={Clock}
        variant={summary.pending === 0 ? 'success' : summary.overdue > 0 ? 'error' : 'warning'}
        change={summary.pending === 0 ? 'All up to date' : 'Awaiting review'}
      />

      {/* Card 2: Overdue — clicking activates the overdue filter */}
      <NavigationMetricCardAlt
        title="Overdue"
        value={summary.overdue.toString()}
        icon={AlertTriangle}
        variant={summary.overdue === 0 ? 'success' : 'error'}
        change={summary.overdue === 0 ? 'None overdue' : 'Needs attention'}
        onClick={summary.overdue > 0 ? () => setOverdueFilter(f => !f) : undefined}
        ariaLabel="Filter to overdue timesheets"
      />

      {/* Card 3: With Anomalies — clicking filters to anomaly rows */}
      <NavigationMetricCardAlt
        title="With Anomalies"
        value={summary.withAnomalies.toString()}
        icon={AlertCircle}
        variant={summary.withAnomalies === 0 ? 'success' : 'warning'}
        change={summary.withAnomalies === 0 ? 'No issues found' : 'Flagged for review'}
        onClick={summary.withAnomalies > 0 ? () => setAnomalyFilter(anomalyFilter === 'with-anomalies' ? 'all' : 'with-anomalies') : undefined}
        ariaLabel="Filter to timesheets with anomalies"
      />

      {/* Card 4: High Severity Issues */}
      <NavigationMetricCardAlt
        title="High Severity"
        value={summary.highSeverity.toString()}
        icon={ShieldAlert}
        variant={summary.highSeverity === 0 ? 'success' : 'error'}
        change={summary.highSeverity === 0 ? 'No critical issues' : 'Require immediate action'}
      />

      {/* Card 5: Total Hours Pending */}
      <NavigationMetricCardAlt
        title="Hours Pending"
        value={`${summary.totalHours.toFixed(1)}h`}
        icon={Timer}
        variant="info"
        change="Total hours to approve"
      />
    </div>
  );
}
